import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle2,
  CircleDollarSign,
  ExternalLink,
  FileText,
  GitPullRequest,
  PackageCheck,
  ShieldAlert
} from "lucide-react";
import type { ReactNode } from "react";
import { harnessSnapshot } from "./harness-snapshot.generated";

type HarnessSnapshotView = {
  generated_at: string;
  agent_runs: {
    today: number;
    week: number;
    total: number;
    by_agent: Record<string, number>;
    success_rate: number;
    recent: Array<{
      id: string;
      agent: string;
      task_id: string;
      status: string;
      started_at: string;
      completed_at: string;
      blocker: string;
      local_only: boolean;
    }>;
  };
  requirements: {
    total: number;
    by_status: Record<string, number>;
    items: Array<{
      id: string;
      title: string;
      status: string;
      priority: string;
      owner_agent: string;
      requirement_doc: string;
      production_url: string;
      blocker: string;
    }>;
  };
  blockers: {
    total: number;
    active: Array<{
      id: string;
      task_id: string;
      source: string;
      message: string;
      created_at: string;
    }>;
  };
  cost: {
    month_to_date_usd: number;
    today_usd: number;
    week_usd: number;
    budget_usd: number;
    by_agent: Record<string, number>;
    ledger_entries: number;
  };
  content: {
    cases: { total: number; by_status: Record<string, number> };
    insights: { total: number; by_status: Record<string, number> };
    podcasts: { configured: boolean; total: number };
    episodes: { configured: boolean; total: number; processed: number };
  };
  releases: Array<{
    id: string;
    kind: string;
    status: string;
    production_url: string;
    req_count: number;
    agent_authored: boolean;
  }>;
  acceptance: {
    total: number;
    accepted: number;
    accepted_with_risk: number;
    blocked: number;
    latest: Array<{
      id: string;
      requirement_id: string;
      release_id: string;
      conclusion: string;
      generated_at: string;
      markdown: string;
      production_url: string;
      github_pr: string;
      checks_passed: number;
      checks_total: number;
    }>;
  };
  mvp_alpha: {
    engineer_alpha_runs: number;
    local_smoke_passed: boolean;
  };
};

const statusLabels: Record<string, string> = {
  new: "new",
  triaged: "triaged",
  ready: "ready",
  in_progress: "in progress",
  pr_opened: "PR opened",
  qa_passed: "QA passed",
  released: "released",
  documented: "documented",
  blocked: "blocked",
  passed: "passed",
  failed: "failed"
};

export function HarnessDashboard() {
  const snapshot = harnessSnapshot as unknown as HarnessSnapshotView;
  const budgetPercent = snapshot.cost.budget_usd
    ? Math.min(100, Math.round((snapshot.cost.month_to_date_usd / snapshot.cost.budget_usd) * 100))
    : 0;
  const agentEntries = Object.entries(snapshot.agent_runs.by_agent).sort((a, b) => b[1] - a[1]);
  const statusEntries = Object.entries(snapshot.requirements.by_status).sort((a, b) => b[1] - a[1]);
  const latestRelease = snapshot.releases[0];

  return (
    <main className="harnessShell">
      <header className="harnessHeader">
        <div>
          <p className="eyebrow">AI Podcast Radar · Harness</p>
          <h1>Harness 运行总览</h1>
          <p className="harnessLead">
            最后更新 {formatTime(snapshot.generated_at)} · engineer alpha runs {snapshot.mvp_alpha.engineer_alpha_runs}
          </p>
        </div>
        <nav className="harnessLinks" aria-label="Harness links">
          <a href="/" aria-label="Open public content homepage">
            <FileText size={16} />
            内容首页
          </a>
          <a href="https://github.com/kunhai-88/ai-podcast-radar" target="_blank" rel="noreferrer">
            <GitPullRequest size={16} />
            GitHub
          </a>
          <a href="https://github.com/kunhai-88/ai-podcast-radar/blob/main/docs/operations/lessons-learned.md" target="_blank" rel="noreferrer">
            <ExternalLink size={16} />
            Lessons
          </a>
        </nav>
      </header>

      <section className="harnessKpis" aria-label="Harness key metrics">
        <Kpi icon={<Bot size={20} />} label="Engineer Runs" value={String(snapshot.agent_runs.by_agent.engineer ?? 0)} detail={`today ${snapshot.agent_runs.today} · week ${snapshot.agent_runs.week}`} />
        <Kpi icon={<ShieldAlert size={20} />} label="Active Blockers" value={String(snapshot.blockers.active.length)} detail={`${snapshot.blockers.total} historical blockers`} tone={snapshot.blockers.active.length ? "bad" : "good"} />
        <Kpi icon={<CircleDollarSign size={20} />} label="Cost MTD" value={`$${snapshot.cost.month_to_date_usd.toFixed(2)}`} detail={`${budgetPercent}% of $${snapshot.cost.budget_usd.toFixed(0)} budget`} tone={budgetPercent >= 90 ? "bad" : budgetPercent >= 80 ? "warn" : "good"} />
        <Kpi icon={<PackageCheck size={20} />} label="MVP-alpha Smoke" value={snapshot.mvp_alpha.local_smoke_passed ? "passed" : "pending"} detail="A + D local flow" tone={snapshot.mvp_alpha.local_smoke_passed ? "good" : "warn"} />
        <Kpi icon={<FileText size={20} />} label="Acceptance Docs" value={String(snapshot.acceptance.total)} detail={`${snapshot.acceptance.accepted} accepted`} tone={snapshot.acceptance.blocked ? "bad" : snapshot.acceptance.accepted ? "good" : "warn"} />
      </section>

      <section className="harnessGrid">
        <Panel title="Agent 运行" icon={<Activity size={18} />} aside={`${Math.round(snapshot.agent_runs.success_rate * 100)}% success`}>
          <div className="metricLine">
            <strong>{snapshot.agent_runs.total}</strong>
            <span>total runs</span>
          </div>
          <BarList entries={agentEntries} total={snapshot.agent_runs.total} />
          <div className="runList">
            {snapshot.agent_runs.recent.slice(0, 5).map((run) => (
              <div className="runRow" key={run.id}>
                <span className={`dot dot-${run.status}`} />
                <div>
                  <strong>{run.agent}</strong>
                  <span>{run.task_id || run.id}</span>
                </div>
                <time>{formatTime(run.completed_at || run.started_at)}</time>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="需求生命周期" icon={<CheckCircle2 size={18} />} aside={`${snapshot.requirements.total} items`}>
          <StatusStrip entries={statusEntries} total={snapshot.requirements.total} />
          <div className="reqList">
            {snapshot.requirements.items.slice(0, 7).map((item) => (
              <a className="reqRow" href={docHref(item.requirement_doc)} target="_blank" rel="noreferrer" key={item.id}>
                <span className={`statusPill status-${item.status}`}>{statusLabels[item.status] ?? item.status}</span>
                <div>
                  <strong>{item.id}</strong>
                  <span>{item.title}</span>
                </div>
                <small>{item.priority} · {item.owner_agent}</small>
              </a>
            ))}
          </div>
        </Panel>

        <Panel title="当前阻塞" icon={<AlertTriangle size={18} />} aside={`${snapshot.blockers.active.length} active`}>
          <div className="blockerList">
            {snapshot.blockers.active.length ? (
              snapshot.blockers.active.slice(0, 5).map((blocker) => (
                <article className="blockerRow" key={blocker.id || `${blocker.task_id}-${blocker.created_at}`}>
                  <div>
                    <strong>{blocker.task_id || "unknown"}</strong>
                    <span>{blocker.source}</span>
                  </div>
                  <p>{blocker.message}</p>
                  <time>{formatTime(blocker.created_at)}</time>
                </article>
              ))
            ) : (
              <p className="emptyState">No active blocker in current local state.</p>
            )}
          </div>
        </Panel>

        <Panel title="成本累计" icon={<CircleDollarSign size={18} />} aside={`${budgetPercent}%`}>
          <div className="costMeter">
            <span style={{ width: `${budgetPercent}%` }} />
          </div>
          <div className="costFacts">
            <span>Today ${snapshot.cost.today_usd.toFixed(2)}</span>
            <span>Week ${snapshot.cost.week_usd.toFixed(2)}</span>
            <span>{snapshot.cost.ledger_entries} ledger rows</span>
          </div>
          <BarList entries={Object.entries(snapshot.cost.by_agent).sort((a, b) => b[1] - a[1])} total={Math.max(snapshot.cost.month_to_date_usd, 1)} money />
        </Panel>

        <Panel title="内容产出" icon={<FileText size={18} />} aside={`${snapshot.content.cases.total + snapshot.content.insights.total} objects`}>
          <div className="contentCounts">
            <ContentCount label="Cases" data={snapshot.content.cases} />
            <ContentCount label="Insights" data={snapshot.content.insights} />
          </div>
          <div className="sourceCounts">
            <span>Podcast catalog: {snapshot.content.podcasts.configured ? snapshot.content.podcasts.total : "not configured"}</span>
            <span>Episode index: {snapshot.content.episodes.configured ? `${snapshot.content.episodes.processed}/${snapshot.content.episodes.total}` : "not configured"}</span>
          </div>
        </Panel>

        <Panel title="最近发布 / 验收" icon={<GitPullRequest size={18} />} aside={latestRelease?.id ?? "none"}>
          <div className="releaseList">
            {snapshot.releases.slice(0, 6).map((release) => (
              <a className="releaseRow" href={release.production_url || "#"} key={`${release.kind}-${release.id}`}>
                <div>
                  <strong>{release.id}</strong>
                  <span>{release.kind} · {release.status}</span>
                </div>
                {release.agent_authored ? <small className="agentMark">agent-authored</small> : <small>{release.req_count} reqs</small>}
              </a>
            ))}
          </div>
          <div className="acceptanceList">
            {snapshot.acceptance.latest.slice(0, 5).map((report) => (
              <a className="acceptanceRow" href={docHref(report.markdown)} target="_blank" rel="noreferrer" key={report.id}>
                <div>
                  <strong>{report.requirement_id}</strong>
                  <span>{report.release_id} · {report.conclusion}</span>
                </div>
                <small>{report.checks_passed}/{report.checks_total}</small>
              </a>
            ))}
          </div>
        </Panel>
      </section>
    </main>
  );
}

function Kpi({ icon, label, value, detail, tone = "neutral" }: { icon: ReactNode; label: string; value: string; detail: string; tone?: "neutral" | "good" | "warn" | "bad" }) {
  return (
    <article className={`harnessKpi tone-${tone}`}>
      <div>{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function Panel({ title, icon, aside, children }: { title: string; icon: ReactNode; aside: string; children: ReactNode }) {
  return (
    <article className="harnessPanel">
      <div className="panelTitle">
        <h2>{icon}{title}</h2>
        <span>{aside}</span>
      </div>
      {children}
    </article>
  );
}

function BarList({ entries, total, money = false }: { entries: [string, number][]; total: number; money?: boolean }) {
  return (
    <div className="barList">
      {entries.slice(0, 7).map(([label, value]) => (
        <div className="barRow" key={label}>
          <span>{label}</span>
          <div><i style={{ width: `${Math.max(4, (value / Math.max(total, 1)) * 100)}%` }} /></div>
          <strong>{money ? `$${value.toFixed(2)}` : value}</strong>
        </div>
      ))}
    </div>
  );
}

function StatusStrip({ entries, total }: { entries: [string, number][]; total: number }) {
  return (
    <div className="statusStrip" aria-label="Requirement status distribution">
      {entries.map(([status, value]) => (
        <span
          key={status}
          className={`status-${status}`}
          style={{ width: `${Math.max(3, (value / Math.max(total, 1)) * 100)}%` }}
          title={`${status}: ${value}`}
        />
      ))}
    </div>
  );
}

function ContentCount({ label, data }: { label: string; data: { total: number; by_status: Record<string, number> } }) {
  return (
    <div className="contentCount">
      <span>{label}</span>
      <strong>{data.total}</strong>
      <small>{Object.entries(data.by_status).map(([status, count]) => `${status} ${count}`).join(" · ") || "no objects"}</small>
    </div>
  );
}

function docHref(path: string) {
  return path ? `https://github.com/kunhai-88/ai-podcast-radar/blob/main/${path}` : "#";
}

function formatTime(value: string) {
  if (!value) return "unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}
